/**
 * The two decisions a failed Ask Data Studio turn has to make, as pure
 * functions so they can be tested. Both were wrong inline.
 *
 * Asking a second question aborts the first one's stream, which makes the first
 * turn's `await` throw. That throw landed in a catch that did two harmful
 * things: it called the failure an error, and it deleted "the last empty turn",
 * which by then was the SECOND question's answer slot.
 *
 * Measured on 2026-09-24: Margo asked "hello" at 23:04:43 and "what is the push
 * setting" eleven seconds later. LangSmith shows the second run succeeded in
 * 4.4s with a 1,277 character reply. She saw an empty box, because the first
 * turn's abort reached into the second turn.
 */

export interface AbortableTurn {
  role: 'user' | 'agent';
  text: string;
  /**
   * Identity, so a turn can be found without counting.
   *
   * Position was tried and is wrong twice over: a stale catch keyed on
   * `length - 1` reaches into a newer turn, and an index captured inside a
   * `setTurns` updater is not assigned until React runs that updater, which can
   * be after the `await` that needs it.
   */
  id: number;
}

/**
 * Whether this failure is just us cancelling our own request.
 *
 * `fetch` rejects with `AbortError` when its signal fires, and asking a follow
 * up fires the previous turn's signal on purpose. Reported as a failure it
 * tells the reader their last question broke when in fact they replaced it.
 *
 * Both spellings are checked: browsers throw a `DOMException` named
 * `AbortError`, while a plain `Error` with that name shows up in tests and in
 * some polyfills.
 */
export function isAbort(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Drop one specific answer turn, by id, when it never received text.
 *
 * A late catch belongs to the turn that started it, and between that turn
 * starting and its failure arriving the reader can have asked something else.
 * Keyed on `all.length - 1` the cleanup deleted the NEW question's answer, the
 * turn currently being streamed into.
 *
 * An unknown id changes nothing, which is what should happen when a cleanup
 * arrives after the transcript was cleared.
 */
export function dropEmptyTurn<T extends AbortableTurn>(
  turns: T[],
  id: number
): T[] {
  const turn = turns.find((candidate) => candidate.id === id);
  if (!turn || turn.text) return turns;
  return turns.filter((candidate) => candidate.id !== id);
}
